import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeSlug from 'rehype-slug';
import remarkGfm from 'remark-gfm';
import Link from '@/components/atoms/link';
import { classnames } from '@/utils/classnames/classnames';

interface MarkdownRendererProps {
  content: string;
  /** Inverts link colour for dark (navy) backgrounds. */
  invert?: boolean;
}

// Strict allowlist: start from rehype-sanitize's safe defaults (no scripts, no
// event handlers, no `style` attributes) and additionally permit the `id`
// attribute so the slugs added by rehype-slug survive for fragment links.
// `clobber: []` keeps those ids verbatim instead of prefixing them.
const sanitizeSchema = {
  ...defaultSchema,
  clobber: [],
  attributes: {
    ...defaultSchema.attributes,
    '*': [...(defaultSchema.attributes?.['*'] ?? []), 'id'],
  },
};

const isExternal = (href: string) => /^(https?:|mailto:|tel:)/i.test(href);

/**
 * Renders Markdown with GFM tables/autolinks through the Electus design system:
 * prose typography for block elements, the locale-aware `Link` atom for in-app
 * anchors, and a horizontal-scroll wrapper around tables on narrow viewports.
 */
const MarkdownRenderer = ({ content, invert = false }: MarkdownRendererProps) => {
  const linkClassName = classnames(
    'rounded-xs underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none',
    // blue-100 (#ECF0FB) on the navy bg-primary clears WCAG AA (~10:1);
    // blue-300 was 2.75:1 and failed the AA AC for body-size links.
    invert ? 'text-blue-100' : 'text-primary',
  );

  const components: Components = {
    a: ({ href, children }) => {
      const target = href ?? '#';
      if (isExternal(target)) {
        return (
          <a href={target} target="_blank" rel="noopener noreferrer" className={linkClassName}>
            {children}
          </a>
        );
      }
      return (
        <Link href={target} className={linkClassName} underlineOnHover={false}>
          {children}
        </Link>
      );
    },
    // Keep wide tables inside the gutter on mobile.
    table: ({ children }) => (
      <div className="overflow-x-auto">
        <table>{children}</table>
      </div>
    ),
    th: ({ children }) => <th scope="col">{children}</th>,
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeSlug, [rehypeSanitize, sanitizeSchema]]}
      components={components}
    >
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;
